/**
 * CustomerHistoryCard - 고객 상담/예약 이력 카드
 *
 * 고객의 상담 이력과 예약 이력을 표시
 */

import { useNavigate } from "react-router-dom";
import { ClipboardList, CalendarDays } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateWithDay } from "@/utils/dateFormatter";
import {
  getConsultResultLabel,
  getConsultResultVariant,
  getAppointmentStatusLabel,
  getAppointmentStatusVariant,
} from "@/constants/status";

interface ConsultItem {
  id: number;
  result: string;
  purpose?: string;
  consultType?: string;
  occurredAt: string;
  summary?: string | null;
}

interface AppointmentItem {
  id: number;
  status: string;
  department?: string;
  appointmentDate: string;
  notes?: string;
}

export interface CustomerHistoryCardProps {
  /**
   * 상담 이력 데이터
   */
  consults?: {
    content: ConsultItem[];
    totalElements: number;
  } | null;
  consultsLoading?: boolean;
  /**
   * 예약 이력 데이터
   */
  appointments?: {
    content: AppointmentItem[];
    totalElements: number;
  } | null;
  appointmentsLoading?: boolean;
  /**
   * 이력 타입 ('both' | 'consults' | 'appointments')
   */
  type?: "both" | "consults" | "appointments";
  /**
   * 카드 없이 내용만 표시
   */
  noCard?: boolean;
  /**
   * 상담 클릭 시 상세 페이지로 이동
   */
  onConsultClick?: (consultId: number) => void;
  /**
   * 예약 클릭 시 핸들러
   */
  onAppointmentClick?: (appointmentId: number) => void;
  /**
   * V1050: 고객 ID (전체 보기 링크용)
   */
  customerId?: number;
}

export function CustomerHistoryCard({
  consults,
  consultsLoading = false,
  appointments,
  appointmentsLoading = false,
  type = "both",
  noCard = false,
  onConsultClick,
  onAppointmentClick,
  customerId,
}: CustomerHistoryCardProps) {
  const navigate = useNavigate();

  // V1050: 전체 보기 - 고객 상세 페이지로 이동
  const handleViewAllConsults = () => {
    if (customerId) {
      navigate(`/customers/${customerId}?tab=consults`);
    }
  };

  const handleConsultClick = (consultId: number) => {
    if (onConsultClick) {
      onConsultClick(consultId);
    } else {
      navigate(`/consults/${consultId}`);
    }
  };

  const consultsContent = (type === "both" || type === "consults") && (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-muted-foreground" />
          <h4 className="font-medium text-sm text-muted-foreground">
            상담 이력 ({consults?.totalElements || 0}건)
          </h4>
        </div>
        {/* V1050: 전체 보기 버튼 - 표시된 건수보다 전체 건수가 많을 때 표시 */}
        {customerId &&
          consults?.totalElements &&
          consults.content.length < consults.totalElements && (
            <button
              onClick={handleViewAllConsults}
              className="text-xs text-primary hover:underline"
            >
              전체 보기
            </button>
          )}
      </div>

      {consultsLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </div>
      ) : consults?.content && consults.content.length > 0 ? (
        <div className="space-y-2">
          {consults.content.map((consult) => (
            <div
              key={consult.id}
              className="p-2.5 bg-muted/30 rounded-lg border border-border/50 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => handleConsultClick(consult.id)}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Badge
                    variant={getConsultResultVariant(consult.result)}
                    className="text-xs"
                  >
                    {getConsultResultLabel(consult.result)}
                  </Badge>
                  <span className="text-sm font-medium">
                    {consult.purpose || consult.consultType || "상담"}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {formatDateWithDay(consult.occurredAt)}
                </span>
              </div>
              {consult.summary && (
                <p className="text-xs text-muted-foreground line-clamp-1">
                  {consult.summary}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-muted-foreground text-sm">
          상담 이력이 없습니다
        </div>
      )}
    </div>
  );

  // V1050: 전체 보기 - 고객 상세 페이지 예약 탭으로 이동
  const handleViewAllAppointments = () => {
    if (customerId) {
      navigate(`/customers/${customerId}?tab=appointments`);
    }
  };

  const appointmentsContent = (type === "both" || type === "appointments") && (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-muted-foreground" />
          <h4 className="font-medium text-sm text-muted-foreground">
            예약 이력 ({appointments?.totalElements || 0}건)
          </h4>
        </div>
        {/* V1050: 전체 보기 버튼 - 표시된 건수보다 전체 건수가 많을 때 표시 */}
        {customerId &&
          appointments?.totalElements &&
          appointments.content.length < appointments.totalElements && (
            <button
              onClick={handleViewAllAppointments}
              className="text-xs text-primary hover:underline"
            >
              전체 보기
            </button>
          )}
      </div>

      {appointmentsLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </div>
      ) : appointments?.content && appointments.content.length > 0 ? (
        <div className="space-y-2">
          {appointments.content.map((appointment) => (
            <div
              key={appointment.id}
              className="p-2.5 bg-muted/30 rounded-lg border border-border/50 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => {
                if (onAppointmentClick) {
                  onAppointmentClick(appointment.id);
                } else {
                  navigate(`/appointments/list?appointmentId=${appointment.id}`);
                }
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Badge
                    variant={getAppointmentStatusVariant(appointment.status)}
                    className="text-xs"
                  >
                    {getAppointmentStatusLabel(appointment.status)}
                  </Badge>
                  <span className="text-sm font-medium">
                    {appointment.department || "진료"}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {formatDateWithDay(appointment.appointmentDate)}
                </span>
              </div>
              {appointment.notes && (
                <p className="text-xs text-muted-foreground line-clamp-1">
                  {appointment.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-muted-foreground text-sm">
          예약 이력이 없습니다
        </div>
      )}
    </div>
  );

  const content = (
    <div className="space-y-4">
      {consultsContent}
      {type === "both" && appointments && <div className="border-t pt-4" />}
      {appointmentsContent}
    </div>
  );

  if (noCard) {
    return content;
  }

  // 각각 별도 카드로 표시
  if (type === "both") {
    return (
      <>
        <Card>
          <CardContent className="p-4">{consultsContent}</CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">{appointmentsContent}</CardContent>
        </Card>
      </>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">{content}</CardContent>
    </Card>
  );
}
